import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import {
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Source as SourceIcon,
  LocationCity as CityIcon,
  Badge as AadhaarIcon,
  CreditCard as PanIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarTodayIcon,
  Pin as PinIcon,
} from '@mui/icons-material';

const SearchForm = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    mobile: '',
    first_name: '',
    last_name: '',
    dob: '',
    source: '',
    city: '',
    aadhaar: '',
    pan: '',
    pincode: '',
  });
  
  // State to track which field is active (for visual indication)
  const [activeField, setActiveField] = useState(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update active field for visual indication
    if (value) {
      if (name === 'first_name' || name === 'last_name') {
        setActiveField('name');
      } else {
        setActiveField(name);
      }
    } else if (
      (name === 'first_name' && !formData.last_name) || 
      (name === 'last_name' && !formData.first_name)
    ) {
      setActiveField(null);
    }

    // Start with completely empty form data
    const emptyForm = {
      email: '',
      mobile: '',
      first_name: '',
      last_name: '',
      dob: '',
      source: '',
      city: '',
      aadhaar: '',
      pan: '',
      pincode: '',
    };
    
    // CASE 1: Mobile number handling
    if (name === 'mobile') {
      // Format the mobile number with +91 prefix
      let mobileNum = value;
      if (mobileNum.startsWith('+91')) {
        mobileNum = mobileNum.substring(3);
      } else if (mobileNum.startsWith('91')) {
        mobileNum = mobileNum.substring(2);
      }
      mobileNum = mobileNum.replace(/\D/g, '');
      const formattedMobile = mobileNum.length > 0 ? '+91' + mobileNum : '';
      
      // Set ONLY the mobile field, all others are empty from emptyForm
      console.log("Mobile changed - clearing all other fields");
      setFormData({ ...emptyForm, mobile: formattedMobile });
      return;
    }
    
    // CASE 2: First name - preserve last name only if it exists
    if (name === 'first_name') {
      console.log("First name changed - preserving only last name if it exists");
      // Start with empty form, set first_name, conditionally keep last_name
      const newData = { ...emptyForm, first_name: value };
      if (formData.last_name) {
        newData.last_name = formData.last_name;
      }
      setFormData(newData);
      return;
    }
    
    // CASE 3: Last name - preserve first name only if it exists
    if (name === 'last_name') {
      console.log("Last name changed - preserving only first name if it exists");
      // Start with empty form, set last_name, conditionally keep first_name
      const newData = { ...emptyForm, last_name: value };
      if (formData.first_name) {
        newData.first_name = formData.first_name;
      }
      setFormData(newData);
      return;
    }
    
    // CASE 4: Any other field - completely clear all other fields
    console.log(`${name} changed - clearing ALL other fields`);
    setFormData({ ...emptyForm, [name]: value });
  };

  // Handle date change from the date picker
  const handleDateChange = (date) => {
    // Update active field
    setActiveField('dob');
    
    // Format the date as YYYY-MM-DD
    let formattedDate = '';
    if (date) {
      try {
        formattedDate = format(date, 'yyyy-MM-dd');
      } catch (error) {
        console.error('Error formatting date:', error);
      }
    }
    
    // Clear all fields and set only the dob field
    const emptyForm = {
      email: '',
      mobile: '',
      first_name: '',
      last_name: '',
      dob: '',
      source: '',
      city: '',
      aadhaar: '',
      pan: '',
      pincode: '',
    };
    
    console.log(`DOB changed to ${formattedDate} - clearing ALL other fields`);
    setFormData({ ...emptyForm, dob: formattedDate });
  };

  // Handle clearing a field
  const handleClear = (field) => {
    // Start with completely empty form
    const emptyForm = {
      email: '',
      mobile: '',
      first_name: '',
      last_name: '',
      dob: '',
      source: '',
      city: '',
      aadhaar: '',
      pan: '',
      pincode: '',
    };
    
    // Reset active field if clearing the active field
    if ((field === 'first_name' || field === 'last_name') && activeField === 'name') {
      if (
        (field === 'first_name' && !formData.last_name) ||
        (field === 'last_name' && !formData.first_name)
      ) {
        setActiveField(null);
      }
    } else if (activeField === field) {
      setActiveField(null);
    }
    
    // Special handling for first_name/last_name pair
    if (field === 'first_name' && formData.last_name) {
      // If clearing first_name but we have last_name, keep only last_name
      setFormData({ ...emptyForm, last_name: formData.last_name });
      return;
    }
    
    if (field === 'last_name' && formData.first_name) {
      // If clearing last_name but we have first_name, keep only first_name
      setFormData({ ...emptyForm, first_name: formData.first_name });
      return;
    }
    
    // Otherwise clear everything
    setFormData(emptyForm);
  };

  // Helper to check if a field should be disabled based on the active field
  const isFieldDisabled = (field) => {
    if (!activeField) return false;
    
    // Name fields logic - allow both first_name and last_name to be active together
    if (activeField === 'name') {
      return field !== 'first_name' && field !== 'last_name';
    }
    
    // For all other fields - disable if not the active field
    return field !== activeField;
  };
  
  // Get helper text based on field status - only show when necessary
  const getHelperText = (field) => {
    // Only show warning messages when fields are disabled
    if (activeField && activeField !== 'name' && field !== activeField) {
      return ""; // No message for disabled fields
    }
    
    if (activeField === 'name' && field !== 'first_name' && field !== 'last_name') {
      return ""; // No message for disabled fields when name is active
    }
    
    if (!activeField) {
      return ""; // No messages in default state
    }
    
    // No helper text for active fields
    return "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Filter out empty fields
    const queryParams = Object.entries(formData)
      .filter(([_, value]) => value.trim() !== '')
      .reduce((acc, [key, value]) => {
        // Trim whitespace
        acc[key] = value.trim();
        return acc;
      }, {});
    
    // Check if at least one field is filled
    if (Object.keys(queryParams).length === 0) {
      alert('Please enter at least one search parameter');
      return;
    }
    
    // Log the search parameters for debugging
    console.log('Searching with parameters:', queryParams);
    
    // Navigate to results page with query params
    navigate({
      pathname: '/results',
      search: new URLSearchParams(queryParams).toString(),
    });
  };

  const getInputIcon = (field) => {
    const icons = {
      email: <EmailIcon />,
      mobile: <PhoneIcon />,
      first_name: <PersonIcon />,
      last_name: <PersonIcon />,
      dob: <CalendarTodayIcon />,
      source: <SourceIcon />,
      city: <CityIcon />,
      aadhaar: <AadhaarIcon />,
      pan: <PanIcon />,
      pincode: <PinIcon />,
    };
    
    return icons[field] || <SearchIcon />;
  };

  // Helper to determine if a field should be highlighted
  const isFieldHighlighted = (field) => {
    return field === 'source' && formData.source !== '';
  };
  
  // Helper to get styling for a field
  const getFieldStyling = (field) => {
    if (isFieldHighlighted(field)) {
      return {
        bgcolor: 'rgba(25, 118, 210, 0.12)',
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: 'primary.main',
            borderWidth: 2,
          },
          '&:hover fieldset': {
            borderColor: 'primary.dark',
          },
        },
        '& .MuiInputLabel-root': {
          color: 'primary.main',
          fontWeight: 'bold',
        },
        '& .MuiInputAdornment-root .MuiSvgIcon-root': {
          color: 'primary.main',
        }
      };
    }
    return {};
  };

  const getInputLabel = (field) => {
    const labels = {
      email: 'Email Address',
      mobile: 'Mobile Number (auto-adds +91)',
      first_name: 'First Name',
      last_name: 'Last Name',
      dob: 'Date of Birth',
      source: 'Source',
      city: 'City',
      aadhaar: 'Aadhaar Number',
      pan: 'PAN Number',
      pincode: 'PIN Code',
    };
    return labels[field] || field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getInputPlaceholder = (field) => {
    const placeholders = {
      email: 'Enter email address',
      mobile: 'Enter mobile number',
      first_name: 'Enter first name',
      last_name: 'Enter last name',
      dob: 'YYYY-MM-DD',
      source: 'Enter data source',
      city: 'Enter city name',
      aadhaar: 'Enter Aadhaar number',
      pan: 'Enter PAN number',
      pincode: 'Enter PIN code',
    };
    
    return placeholders[field] || `Enter ${field.replace('_', ' ')}`;
  };

  const renderBasicSearch = () => (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      sx={{ mt: 3 }} 
      autoComplete="off"
      data-form-type="other"
      autoCorrect="off"
      spellCheck="false"
    >
      {/* Clean, professional search instruction */}
      <Box sx={{ 
        mb: 3, 
        p: 1.5, 
        bgcolor: 'rgba(25, 118, 210, 0.06)', 
        borderRadius: 1,
        border: '1px solid rgba(25, 118, 210, 0.2)',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
          Search using one field at a time. First and last names can be used together.
        </Typography>
      </Box>
      
      <input type="text" style={{ display: 'none' }} />
      <input type="password" style={{ display: 'none' }} />
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="email"
            label="Email Address"
            value={formData.email}
            onChange={handleChange}
            variant="outlined"
            autoComplete="chrome-off"
            autoCorrect="off"
            spellCheck="false"
            disabled={isFieldDisabled('email')}
            helperText={getHelperText('email')}
            inputProps={{
              autoComplete: "new-email",
              "data-form-type": "other",
              "data-lpignore": "true"
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color={activeField === 'email' ? 'primary' : 'inherit'} />
                </InputAdornment>
              ),
              endAdornment: formData.email && (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleClear('email')} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              ...(activeField === 'email' && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="mobile"
            label="Mobile Number (auto-adds +91)"
            value={formData.mobile}
            onChange={handleChange}
            variant="outlined"
            autoComplete="chrome-off"
            autoCorrect="off"
            spellCheck="false"
            disabled={isFieldDisabled('mobile')}
            helperText={getHelperText('mobile')}
            inputProps={{
              autoComplete: "new-phone",
              "data-form-type": "other",
              "data-lpignore": "true"
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color={activeField === 'mobile' ? 'primary' : 'inherit'} />
                </InputAdornment>
              ),
              endAdornment: formData.mobile && (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleClear('mobile')} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              ...(activeField === 'mobile' && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="first_name"
            label="First Name"
            value={formData.first_name}
            onChange={handleChange}
            variant="outlined"
            autoComplete="chrome-off"
            autoCorrect="off"
            spellCheck="false"
            disabled={isFieldDisabled('first_name')}
            helperText={getHelperText('first_name')}
            inputProps={{
              autoComplete: "new-fname",
              "data-form-type": "other",
              "data-lpignore": "true"
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color={activeField === 'name' ? 'primary' : 'inherit'} />
                </InputAdornment>
              ),
              endAdornment: formData.first_name && (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleClear('first_name')} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              ...(activeField === 'name' && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            name="last_name"
            label="Last Name"
            value={formData.last_name}
            onChange={handleChange}
            variant="outlined"
            autoComplete="chrome-off"
            autoCorrect="off"
            spellCheck="false"
            disabled={isFieldDisabled('last_name')}
            helperText={getHelperText('last_name')}
            inputProps={{
              autoComplete: "new-lname",
              "data-form-type": "other",
              "data-lpignore": "true"
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color={activeField === 'name' ? 'primary' : 'inherit'} />
                </InputAdornment>
              ),
              endAdornment: formData.last_name && (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleClear('last_name')} edge="end">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              ...(activeField === 'name' && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={<SearchIcon />}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              background: 'linear-gradient(45deg, #6C63FF 30%, #5A54D4 90%)',
              boxShadow: '0 3px 5px 2px rgba(108, 99, 255, .3)',
            }}
          >
            Search
          </Button>
        </motion.div>
      </Box>
    </Box>
  );

  const renderAdvancedSearch = () => (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      sx={{ mt: 3 }} 
      autoComplete="off"
      data-form-type="other"
      autoCorrect="off"
      spellCheck="false"
    >
      {/* Clean, professional search instruction */}
      <Box sx={{ 
        mb: 3, 
        p: 1.5, 
        bgcolor: 'rgba(25, 118, 210, 0.06)', 
        borderRadius: 1,
        border: '1px solid rgba(25, 118, 210, 0.2)',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
          Search using one field at a time. First and last names can be used together.
        </Typography>
      </Box>
      
      {/* Hidden fields to prevent autofill */}
      <input type="text" style={{ display: 'none' }} />
      <input type="password" style={{ display: 'none' }} />
      
      <Grid container spacing={2}>
        {Object.keys(formData).map((field) => (
          <Grid item xs={12} md={6} key={field}>
            {field === 'dob' ? (
              // Special handling for DOB field - show date picker
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={getInputLabel(field)}
                  value={formData.dob ? new Date(formData.dob) : null}
                  onChange={handleDateChange}
                  disabled={isFieldDisabled(field)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      name: field,
                      helperText: getHelperText(field),
                      disabled: isFieldDisabled(field),
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            {React.cloneElement(getInputIcon(field), { 
                              color: activeField === field ? 'primary' : 'inherit' 
                            })}
                          </InputAdornment>
                        ),
                        endAdornment: formData[field] && (
                          <InputAdornment position="end">
                            <IconButton onClick={() => handleClear(field)} edge="end">
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                      sx: {
                        ...getFieldStyling(field),
                        ...(activeField === field && {
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'primary.main',
                              borderWidth: 2,
                            },
                          },
                        })
                      },
                      inputProps: {
                        autoComplete: `new-${field}`,
                        "data-form-type": "other",
                        "data-lpignore": "true"
                      }
                    },
                  }}
                />
              </LocalizationProvider>
            ) : (
              // Regular text field for other fields
              <TextField
                fullWidth
                name={field}
                label={getInputLabel(field)}
                placeholder={getInputPlaceholder(field)}
                value={formData[field]}
                onChange={handleChange}
                variant="outlined"
                autoComplete="chrome-off"
                autoCorrect="off"
                spellCheck="false"
                disabled={isFieldDisabled(field)}
                helperText={getHelperText(field)}
                inputProps={{
                  autoComplete: `new-${field}`,
                  "data-form-type": "other",
                  "data-lpignore": "true"
                }}
                sx={{
                  ...getFieldStyling(field),
                  ...(
                    (activeField === field) || 
                    (activeField === 'name' && (field === 'first_name' || field === 'last_name'))
                  ) && {
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: 2,
                      },
                    },
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {React.cloneElement(getInputIcon(field), { 
                        color: (
                          (activeField === field) || 
                          (activeField === 'name' && (field === 'first_name' || field === 'last_name'))
                        ) ? 'primary' : 'inherit' 
                      })}
                    </InputAdornment>
                  ),
                  endAdornment: formData[field] && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleClear(field)} edge="end">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={<SearchIcon />}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 3,
              background: 'linear-gradient(45deg, #6C63FF 30%, #5A54D4 90%)',
              boxShadow: '0 3px 5px 2px rgba(108, 99, 255, .3)',
            }}
          >
            Search
          </Button>
        </motion.div>
      </Box>
    </Box>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Paper
        elevation={6}
        sx={{
          p: 4,
          borderRadius: 4,
          background: 'rgba(30, 30, 30, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          centered
          sx={{ mb: 3 }}
          TabIndicatorProps={{
            style: {
              background: 'linear-gradient(45deg, #6C63FF 30%, #FF6584 90%)',
            }
          }}
        >
          <Tab label="Basic Search" />
          <Tab label="Advanced Search" />
        </Tabs>
        
        {activeTab === 0 ? renderBasicSearch() : renderAdvancedSearch()}
        
      </Paper>
    </motion.div>
  );
};

export default SearchForm;